import { createSelector } from "@reduxjs/toolkit";

import { ENVIRONMENT_TYPES } from "Constants/constants";
import { RootState } from "Store/configureStore";

import type { Invitation, InvitationEnvironmentType } from "@packages/client";

const selectSelf = (state: RootState) => state.invitation;

const extractProjectId = (_: RootState, { projectId }: { projectId: string }) =>
  projectId;

export const selectProjectInvitations = createSelector(
  [selectSelf, extractProjectId],
  (invitations, projectId) => {
    return invitations.data
      .filter(invitation => invitation.projectId === projectId)
      .map(augmentEnvironments);
  }
);

export const selectEmailsFromProjectInvitations = createSelector(
  selectProjectInvitations,
  invitations => invitations.map(invitation => invitation.email)
);

const augmentEnvironments = (invitation: Invitation) => {
  if (invitation.role != "admin") {
    ENVIRONMENT_TYPES.forEach(environmentType => {
      const t = invitation.environments.find(x => x.type === environmentType);
      if (!t) {
        invitation.environments.push({
          type: environmentType,
          role: "no-access"
        });
        invitation.environments.sort(environmentTypesSorter);
      }
    });
  }

  return invitation;
};

// sorts the environment types in the order the UI wants them
// Production > Staging > Development
type HasType = { type: InvitationEnvironmentType };
const types = {
  production: 0,
  staging: 1,
  development: 2
};
export const environmentTypesSorter = (a: HasType, b: HasType) =>
  types[a.type] - types[b.type];

export const invitationErrorsSelector = (state: RootState) =>
  state.invitation.errors;

const extractInvitationId = (
  _: RootState,
  { invitationId }: { invitationId: string }
) => invitationId;

export const singleInvitationSelector = createSelector(
  [selectSelf, extractInvitationId],
  (invitations, invitationId) => {
    const singleInvitation = invitations.data.find(
      invitation => invitation.id === invitationId
    );

    return singleInvitation ? augmentEnvironments(singleInvitation) : null;
  }
);

export const invitationsLoadingSelector = (state: RootState) =>
  state.invitation.loading;

export const statusSelector = (state: RootState) => state.invitation.status;
